import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { Modal, Field, Btn, inputCls } from "@/dashboard/ui";
import { Upload, FileSpreadsheet } from "lucide-react";

const FIELD_LABELS = {
  full_name: "Full name", email: "Email", phone: "Phone", company: "Company",
  source: "Source", status: "Status", notes: "Notes", call_consent: "Call consent (yes/no)",
  consent_notes: "Consent evidence", follow_up_date: "Follow-up date",
};

export default function LeadImport({ onClose, onDone }) {
  const [step, setStep] = useState("upload"); // upload | map | result
  const [busy, setBusy] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/leads/import/preview", fd);
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      setCsvText(await file.text());
      setStep("map");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/leads/import/commit", { csv_text: csvText, mapping });
      setResult(data);
      setStep("result");
      onDone();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal testid="lead-import-modal" title="Import leads from CSV" onClose={onClose}>
      {step === "upload" && (
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            Upload a CSV with columns like name, phone and email. Nothing is imported until you review the mapping and confirm. Duplicate phone numbers or emails are skipped.
          </p>
          <label
            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-neutral-300 bg-neutral-50/60 px-6 py-10 text-center hover:bg-neutral-50"
            data-testid="import-dropzone"
          >
            <FileSpreadsheet size={22} className="text-neutral-400" />
            <span className="text-sm font-semibold text-[#111111]">{busy ? "Reading…" : "Choose a CSV file"}</span>
            <span className="text-xs text-neutral-400">UTF-8 CSV, up to 2MB</span>
            <input type="file" accept=".csv,text/csv" className="hidden" data-testid="import-file-input"
              onChange={(e) => handleFile(e.target.files?.[0])} />
          </label>
          {error && <p className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600" data-testid="import-error">{error}</p>}
          <button
            type="button"
            data-testid="import-template-download"
            className="text-xs font-semibold text-neutral-500 underline underline-offset-2 hover:text-[#111111]"
            onClick={() => {
              const csv = "full_name,phone,email,company,source,status,notes,call_consent,consent_notes,follow_up_date\n"
                + "Jane Example,+447700900123,jane@example.com,Example Salon,Website,New,Met at expo,no,,\n";
              const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = "savy-kerno-leads-template.csv";
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Download CSV template
          </button>
        </div>
      )}

      {step === "map" && preview && (
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            {preview.row_count} rows found. Match each CSV column to a lead field — unmatched columns are ignored.
          </p>
          <div className="space-y-2">
            {preview.headers.map((h) => (
              <div key={h} className="grid grid-cols-2 items-center gap-3">
                <span className="truncate rounded-lg bg-neutral-100 px-3 py-2 text-xs font-semibold text-neutral-700">{h}</span>
                <select className={inputCls} value={mapping[h] || ""} data-testid={`import-map-${h.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value || null }))}>
                  <option value="">Ignore</option>
                  {Object.entries(FIELD_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </div>
            ))}
          </div>
          {preview.sample?.[0] && (
            <p className="text-xs text-neutral-400">
              First row: {Object.entries(preview.sample[0]).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(" · ")}
            </p>
          )}
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
            Importing never starts calls or marketing. Call consent is only recorded for rows with an explicit "yes" in a mapped consent column.
          </div>
          {error && <p className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600" data-testid="import-error">{error}</p>}
          <div className="flex justify-end gap-2.5">
            <Btn type="button" variant="ghost" onClick={() => setStep("upload")}>Back</Btn>
            <Btn type="button" onClick={commit} disabled={busy || !Object.values(mapping).includes("full_name")} data-testid="import-commit-button">
              <Upload size={15} /> {busy ? "Importing…" : `Import ${preview.row_count} rows`}
            </Btn>
          </div>
        </div>
      )}

      {step === "result" && result && (
        <div className="space-y-4" data-testid="import-result">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-xl bg-neutral-50 p-4">
              <p className="text-2xl font-bold text-[#111111]" data-testid="import-imported-count">{result.imported}</p>
              <p className="text-xs text-neutral-500">Imported</p>
            </div>
            <div className="rounded-xl bg-neutral-50 p-4">
              <p className="text-2xl font-bold text-[#111111]" data-testid="import-dupe-count">{result.duplicates_skipped}</p>
              <p className="text-xs text-neutral-500">Duplicates skipped</p>
            </div>
            <div className="rounded-xl bg-neutral-50 p-4">
              <p className="text-2xl font-bold text-[#111111]" data-testid="import-error-count">{result.errors.length}</p>
              <p className="text-xs text-neutral-500">Rows with errors</p>
            </div>
          </div>
          {result.errors.length > 0 && (
            <ul className="max-h-32 space-y-1 overflow-y-auto rounded-xl border border-neutral-200 p-3 text-xs text-neutral-600" data-testid="import-error-list">
              {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
            </ul>
          )}
          <p className="text-xs text-neutral-400">Batch ID: {result.batch_id} — use it to filter these leads in calling campaigns.</p>
          <div className="flex justify-end">
            <Btn type="button" onClick={onClose} data-testid="import-done-button">Done</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}
